import { useCallback } from 'react';
import type { Component } from 'react';
import type { View } from 'react-native';
import { measure, useAnimatedRef } from 'react-native-reanimated';
import { scheduleOnRN, scheduleOnUI } from 'react-native-worklets';

/**
 * Measures a view's window (screen-absolute) rect on the UI thread via
 * Reanimated, as a drop-in replacement for RN's JS-thread `measureInWindow()` /
 * `measure()`.
 *
 * Those imperative measures route through the legacy Fabric path
 * `UIManager::findShadowNodeByTag_DEPRECATED` → `ShadowTreeRegistry::enumerate`,
 * which walks the shadow tree on the JS thread and can segfault on Android when
 * it races a concurrent Fabric commit during scroll/teardown (RN 0.85.x,
 * GROKIO-APP-145). Reanimated's `measure()` reads the shadow node directly on
 * the UI thread and never touches that path.
 *
 * Attach the returned `ref` to the target view (`View` / `Pressable` /
 * `Animated.*`), then call `measureInWindow(cb)`. `cb` receives screen-absolute
 * `x, y, width, height` on the JS thread — the same coordinates RN's
 * `measureInWindow` reports, and the same as `measure()`'s `pageX/pageY`. The
 * callback is skipped when the node can't be measured (e.g. not yet laid out),
 * mirroring the silent no-op of the native methods.
 */
export function useWindowMeasure<TRef extends Component = View>() {
  const ref = useAnimatedRef<TRef>();

  const measureInWindow = useCallback(
    (onMeasured: (x: number, y: number, width: number, height: number) => void) => {
      scheduleOnUI(() => {
        'worklet';
        const measured = measure(ref);
        if (measured === null) return;
        scheduleOnRN(onMeasured, measured.pageX, measured.pageY, measured.width, measured.height);
      });
    },
    [ref]
  );

  return { ref, measureInWindow };
}
